import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Check, MapPin } from "lucide-react";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { FaqAccordion } from "@/components/FaqAccordion";
import type { RangpurArea } from "@/content/rangpur-areas";
import type { Location } from "@/content/locations";
import type { RangpurAreaView } from "./types";

/** Recommended services paired with the closest neighbourhoods. */
export function AreaRecommendations({
  a,
  relatedServices,
  neighbors,
}: {
  a: RangpurArea;
  relatedServices: RangpurAreaView["relatedServices"];
  neighbors: RangpurAreaView["neighbors"];
}) {
  return (
    <Section
      eyebrow={`Recommended for ${a.name}`}
      title={
        <>
          Best next steps for <span className="text-gradient">{a.name}.</span>
        </>
      }
    >
      <p className="max-w-2xl text-muted-foreground -mt-6 mb-8">
        The services that move the needle fastest for {a.name}-area businesses, paired with the
        closest neighborhoods where the same buyer journeys overlap.
      </p>
      <div className="grid lg:grid-cols-2 gap-6">
        <Reveal>
          <div className="rounded-3xl border border-border p-7 bg-card h-full">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Most relevant services
            </div>
            <div className="mt-1 font-display text-xl">For businesses near {a.name}</div>
            <ul className="mt-5 space-y-3">
              {relatedServices.slice(0, 3).map((r) => (
                <li key={"rec-" + r.label + r.to + (r.hash ?? "")}>
                  <Link
                    to={r.to}
                    hash={r.hash}
                    className="group flex items-start gap-3 rounded-2xl border border-border p-4 hover:border-primary transition"
                  >
                    <span className="mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition">
                      <ArrowUpRight size={14} />
                    </span>
                    <div>
                      <div className="font-medium">{r.label}</div>
                      <p className="mt-0.5 text-sm text-muted-foreground">{r.why}</p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="rounded-3xl border border-border p-7 bg-card h-full">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Nearby neighborhoods
            </div>
            <div className="mt-1 font-display text-xl">Closest to {a.name}</div>
            <ul className="mt-5 space-y-3">
              {neighbors.slice(0, 3).map(({ area: n, km }) => (
                <li key={"rec-near-" + n.slug}>
                  <Link
                    to="/seo-services/rangpur/$area"
                    params={{ area: n.slug }}
                    className="group flex items-start gap-3 rounded-2xl border border-border p-4 hover:border-primary transition"
                  >
                    <span className="mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition">
                      <MapPin size={14} />
                    </span>
                    <div className="min-w-0">
                      <div className="font-medium">SEO near {n.name}</div>
                      <p className="mt-0.5 text-sm text-muted-foreground">
                        ≈ {km.toFixed(1)} km from {a.name}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/seo-services/$location"
              params={{ location: "rangpur" }}
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            >
              See all Rangpur areas <ArrowUpRight size={14} />
            </Link>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

function Chips({ items }: { items: string[] }) {
  return (
    <div className="mt-6 flex flex-wrap gap-3">
      {items.map((item) => (
        <span key={item} className="rounded-full border border-border bg-card px-4 py-2 text-sm">
          {item}
        </span>
      ))}
    </div>
  );
}

/** Business types and landmarks served around the area. */
export function AreaLocalContext({ a }: { a: RangpurArea }) {
  return (
    <Section
      eyebrow="Local context"
      title={
        <>
          Who I work with around <span className="text-gradient">{a.name}.</span>
        </>
      }
    >
      <div className="grid lg:grid-cols-2 gap-10">
        <Reveal>
          <h3 className="font-display text-2xl">Business types</h3>
          <Chips items={a.businesses} />
        </Reveal>
        <Reveal delay={0.1}>
          <h3 className="font-display text-2xl">Landmarks & corridors</h3>
          <Chips items={a.landmarks} />
        </Reveal>
      </div>
    </Section>
  );
}

/** Four-step hyperlocal playbook phrased around the area name. */
export function AreaPlaybook({ a }: { a: RangpurArea }) {
  const steps = [
    `Optimize your Google Business Profile for "${a.name}" + your category — name, categories, services, photos, posts, Q&A.`,
    `Build a hyperlocal landing page on your site targeting "${a.name}" with embedded map, photos, hours, and FAQ schema.`,
    `Run a citation + NAP cleanup across Bangladeshi directories so "${a.name}" address signals are consistent.`,
    `Stack reviews and review-response velocity from real ${a.name}-area customers — reviews are one of the signals Google says it uses for local results.`,
  ];
  return (
    <Section
      eyebrow="The hyperlocal playbook"
      title={
        <>
          How I win <span className="text-gradient">"{a.name}" searches.</span>
        </>
      }
    >
      <div className="grid md:grid-cols-2 gap-6">
        {steps.map((t, i) => (
          <Reveal key={i} delay={i * 0.04}>
            <div className="flex gap-4 rounded-3xl border border-border p-7 bg-card h-full">
              <span className="mt-1 inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                <Check size={16} />
              </span>
              <p className="text-muted-foreground leading-relaxed">{t}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

/** FAQ accordion — emits FAQPage JSON-LD via FaqAccordion. */
export function AreaFaq({ a, faqs }: { a: RangpurArea; faqs: RangpurAreaView["faqs"] }) {
  return (
    <Section
      eyebrow="FAQ"
      title={
        <>
          Common questions about SEO near <span className="text-gradient">{a.name}.</span>
        </>
      }
    >
      <FaqAccordion items={faqs} idPrefix={`faq-rangpur-${a.slug}`} />
    </Section>
  );
}

export function AreaMap({
  a,
  city,
  mapEmbedSrc,
  mapsLink,
}: {
  a: RangpurArea;
  city: Location;
  mapEmbedSrc: string;
  mapsLink: string;
}) {
  return (
    <Section
      eyebrow="On the map"
      title={
        <>
          Around <span className="text-gradient">{a.name}.</span>
        </>
      }
    >
      <Reveal>
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2 rounded-3xl overflow-hidden border border-border bg-card">
            <iframe
              title={`Map of ${a.name}, Rangpur`}
              src={mapEmbedSrc}
              className="w-full aspect-[16/10] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <div className="rounded-3xl border border-border p-8 bg-card">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">City</div>
            <div className="mt-2 font-display text-xl">Rangpur</div>
            <div className="mt-5 text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Division
            </div>
            <div className="mt-2 font-display text-xl">{city.region}</div>
            <div className="mt-5 text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Country
            </div>
            <div className="mt-2 font-display text-xl">{city.country}</div>
            <a
              href={mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            >
              Open in Google Maps <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </Reveal>
    </Section>
  );
}
